import {useDrag} from '@use-gesture/react';

import {useState} from 'react';

import {isNil} from 'ramda';

import {FinancingCompositionScene} from '@dms/api/shared';

import {compositionEditorLimits} from '../constants/compositionEditorLimits';
import {LayerBox} from '../types/CompositionEditorAction';
import {ResizeHandle} from '../types/ResizeHandle';
import {applyResizeHandle} from '../utils/applyResizeHandle';
import {clampLayerToCanvas} from '../utils/clampLayerToCanvas';

type LayerDragParams = {
  layer: LayerBox;
  canvas: Pick<FinancingCompositionScene['canvas'], 'width' | 'height'>;
  scale: number;
  onSelect: () => void;
  onCommit: (box: LayerBox) => void;
};

const snapToCenter = (value: number, center: number) =>
  Math.abs(value - center) <= compositionEditorLimits.centerSnapThreshold ? center : value;

/**
 * Pointer drag/resize for one canvas layer. The transient box lives here during the gesture and
 * is committed to the editor state once on release, so a drag produces a single history entry.
 * Resize handles share one gesture; the active handle is read from the handle element's
 * `data-handle` attribute so eight handles do not need eight hook instances.
 */
export function useLayerDrag(params: LayerDragParams) {
  const [transientBox, setTransientBox] = useState<LayerBox | null>(null);

  const finish = (box: LayerBox, last: boolean) => {
    if (last) {
      setTransientBox(null);
      params.onCommit(box);
      return;
    }
    setTransientBox(box);
  };

  const bindMove = useDrag(
    ({movement: [mx, my], first, last, event}) => {
      event.stopPropagation();
      if (first) {
        params.onSelect();
      }
      const centerX = Math.round((params.canvas.width - params.layer.width) / 2);
      const centerY = Math.round((params.canvas.height - params.layer.height) / 2);
      finish(
        clampLayerToCanvas(
          {
            ...params.layer,
            x: snapToCenter(params.layer.x + mx / params.scale, centerX),
            y: snapToCenter(params.layer.y + my / params.scale, centerY),
          },
          params.canvas
        ),
        last
      );
    },
    {filterTaps: true}
  );

  const bindResize = useDrag(
    ({movement: [mx, my], last, event, target}) => {
      event.stopPropagation();
      const handle = readResizeHandle(target);
      if (isNil(handle)) {
        return;
      }
      finish(
        clampLayerToCanvas(
          applyResizeHandle(params.layer, handle, mx / params.scale, my / params.scale),
          params.canvas
        ),
        last
      );
    },
    {filterTaps: true}
  );

  return {
    box: transientBox ?? params.layer,
    isDragging: !isNil(transientBox),
    bindMove,
    bindResize,
  };
}

const RESIZE_HANDLE_VALUES: ReadonlySet<string> = new Set([
  'n',
  'ne',
  'e',
  'se',
  's',
  'sw',
  'w',
  'nw',
]);

const readResizeHandle = (target: EventTarget | null): ResizeHandle | null => {
  if (!(target instanceof HTMLElement)) {
    return null;
  }
  const handle = target.dataset.handle;
  return isResizeHandle(handle) ? handle : null;
};

const isResizeHandle = (value: string | undefined): value is ResizeHandle =>
  !isNil(value) && RESIZE_HANDLE_VALUES.has(value);
